import { COVERAGE_STATUS_LABEL } from '@/modules/projects/components/StatementCoverage/constants'
import type { CoverageStatus } from '@/modules/projects/components/StatementCoverage/types'
import { cn } from '@/utils'
import {
  COVERAGE_SWATCH_CLASS,
  coverageTextClass,
  type StatusFor,
} from './coverage'
import type { MapSpan as Span } from './map'
import {
  DROPPING_ON,
  fadedClass,
  type Highlight,
  type MapActions,
  OPEN_RING,
  TARGET_UNDER_POINTER,
} from './mapView'
import { LINKED_BAR } from './memberLink'
import { formatPeriod, formatPeriodShort } from './period'
import { StatementButton } from './StatementButton'
import type { StatementView } from './types'

interface MapSpanProps {
  span: Span
  laneLabel: string
  laneNumber: string | null
  choosing: boolean
  openStatementId: string | null
  highlight: Highlight
  statusFor: StatusFor
  actions: MapActions
}

// The bar is one line, so the page count is a bare number. A bar wide enough to hold them also
// prints its dates. The full wording is in the title.
const WIDE_ENOUGH_FOR_DATES = 3

const pageCount = (view: StatementView) =>
  view.pages.length === 0 ? '–' : String(view.pages.length)

export const spanLabel = (span: Span) => {
  const months = span.toMonth - span.fromMonth + 1
  const period = span.view.statement.period
  return months >= WIDE_ENOUGH_FOR_DATES && period
    ? `${formatPeriodShort(period)} · ${pageCount(span.view)}`
    : pageCount(span.view)
}

export const spanColumns = (span: Span) =>
  `${span.fromMonth + 2} / ${span.toMonth + 3}`

export const spanTitle = (
  laneLabel: string,
  view: StatementView,
  status: CoverageStatus,
  choosing: boolean,
) =>
  `${laneLabel}, ${formatPeriod(view.statement.period)}, ${view.pages.length} pages, ${COVERAGE_STATUS_LABEL[status].toLowerCase()}${choosing ? '. Put the selected pages here.' : ''}`

// A statement is a bar across every month it owns, in the colour the coverage grid gives it. The
// bar loses its rounded end on the side it carries on past New Year.
export const MapSpan = ({
  span,
  laneLabel,
  laneNumber,
  choosing,
  openStatementId,
  highlight,
  statusFor,
  actions,
}: MapSpanProps) => {
  const { view } = span
  const status = statusFor(view.statement.id, laneNumber)
  return (
    <div
      className={cn('h-8', fadedClass(highlight, status))}
      style={{
        gridColumn: spanColumns(span),
        gridRow: span.track + 1,
      }}
    >
      <StatementButton
        view={view}
        choosing={choosing}
        actions={actions}
        className={cn(
          'flex size-full cursor-pointer items-center justify-center overflow-hidden rounded-md px-1 font-medium tabular-nums text-[11px] leading-none transition-colors',
          span.continuesBefore && 'rounded-l-none',
          span.continuesAfter && 'rounded-r-none',
          COVERAGE_SWATCH_CLASS[status],
          coverageTextClass(status),
          openStatementId === view.statement.id && OPEN_RING,
          LINKED_BAR,
          choosing ? TARGET_UNDER_POINTER : 'hover:opacity-80',
          DROPPING_ON,
        )}
        title={spanTitle(laneLabel, view, status, choosing)}
      >
        <span className="truncate">{spanLabel(span)}</span>
      </StatementButton>
    </div>
  )
}
